/**
 * 한국어 UI 문구 — **이 파일이 문구 구조의 정본이다.**
 *
 * ⚠️ 지금 문구는 **기능을 설명하는 임시 표현**이다. 서비스 이름·컨셉·용어
 * (고른 것을 뭐라 부를지, 프로필을 뭐라 부를지)가 정해지지 않아 브랜딩과
 * 마케팅 카피를 일부러 뺐다. 방향이 정해지면 **이 파일과 en.ts만** 고치면
 * 화면 전체 표현이 바뀐다 — 컴포넌트에는 문구가 박혀 있지 않다.
 *
 * 다른 언어 파일은 여기서 뽑은 타입(`Messages`)을 따르므로, 키를 빠뜨리면
 * 컴파일이 안 된다. 번역 누락이 화면의 빈 칸으로 드러나는 일을 막는다.
 *
 * `{이름}` 자리는 `t('키', { 이름: 값 })`으로 채운다.
 */

export const ko = {
  common: {
    // 로고에 찍히는 이름. 읽는 소리는 같고 **적는 글자만** 다르다
    appName: '라타라',
    tagline: '',
    save: '저장',
    cancel: '취소',
    delete: '지우기',
    edit: '수정',
    close: '닫기',
    confirm: '확인',
    loading: '불러오는 중…',
    retry: '다시 시도',
    copied: '복사했어요',
    /** 바닥글 정보 칸의 라벨 */
    email: '이메일',
  },

  nav: {
    myTimeline: '내 타임라인',
    myPicks: '내 목록',
    backToProfile: '내 프로필로',
    settings: '설정',
    signIn: '로그인',
    signOut: '로그아웃',
    skipToContent: '본문 바로가기',
  },

  landing: {
    /*
     * 한 문구뿐이다. "한 장"이 타임라인과 취향을 **둘 다** 담는다 —
     * 둘로 나눠 걸면 서비스가 두 개인 것처럼 보이고, 들어오기도 전에
     * 무엇을 고를지 정하게 만든다.
     */
    title: '내 인생을 한 장에',
    start: '시작하기',
  },

  anonymous: {
    empty: '아직 고른 게 없어요.',
    title: '내 목록',
    saveLead: '로그인하면 저장되고, 주소로 공유할 수 있어요.',
    saveCta: '로그인',
  },

  picks: {
    addOne: '추가',
    myTitle: '내 목록',
    count: '{count}개',
    empty: '아직 고른 게 없어요.',
    itemName: '어떤 {category}인가요?',
    itemDescription: '왜 이걸 골랐나요?',
    itemImage: '사진',
    deleteThis: '지우기',
    reorderHint: '길게 눌러 끌면 순서를 바꿀 수 있어요. 키보드로는 Space로 집고 화살표로 옮깁니다.',
    reorderSaved: '순서를 저장했어요',
    reorderStale: '목록이 그 사이 바뀌었어요. 다시 불러왔습니다.',
    deleteConfirm: '{category}, 목록에서 지울까요?',
    deleteConfirmBody: '적어둔 이름과 설명도 같이 없어져요.',
    saved: '저장했어요',
  },

  category: {
    searchTitle: '어떤 카테고리인가요?',
    searchPlaceholder: '{example} …',
    searchHint: '검색해서 카테고리를 고르세요.',
    matchedVia: "'{keyword}'로 찾음",
    noResults: "'{query}'에 맞는 카테고리가 없어요.",
    requestCta: '이 카테고리 요청하기',
    requestMore: '찾는 카테고리가 없나요? 요청하기',
    alreadyPicked: '이미 고름',
  },

  image: {
    choose: '사진 고르기',
    change: '바꾸기',
    remove: '사진 지우기',
    processing: '사진을 줄이는 중',
    uploading: '사진 올리는 중',
    tooLarge: '사진이 너무 큽니다. 다른 사진을 골라주세요.',
    unreadable: '이 파일은 읽을 수 없어요. 다른 사진을 골라주세요.',
    uploadFailed: '사진을 올리지 못했어요. 다시 시도해주세요.',
    cropTitle: '사진 자르기',
  },

  categoryRequest: {
    title: '카테고리 요청',
    lead: '원하는 카테고리가 없나요? 알려주시면 검토해서 추가합니다.',
    nameLabel: '원하는 카테고리',
    namePlaceholder: '예: 커피 원두',
    noteLabel: '추가 설명',
    notePlaceholder: '어떤 걸 고르고 싶으신가요? (선택)',
    submit: '요청하기',
    doneTitle: '요청을 받았어요',
    doneBody: '검토 후 추가되면 알려드릴게요.',
    doneCount: '같은 요청이 {count}번 들어왔어요.',
  },

  auth: {
    signInTitle: '로그인',
    signInLead: '저장하고 공유하려면 로그인이 필요합니다.',
    google: 'Google로 계속하기',
    devSignInAs: '개발용 로그인 — {name}',
    devSignInHint: '로컬에서만 보여요. 둘 중 하나로 들어가면 인연 요청을 주고받아 볼 수 있어요.',
    signingIn: '로그인 중…',
  },

  signUp: {
    title: '프로필 만들기',
    lead: '공유할 주소와 표시할 이름을 정해주세요.',
    userIdLabel: '내 주소',
    userIdHelp: '소문자, 숫자, - , _ 를 쓸 수 있어요. 나중에 설정에서 바꿀 수 있어요.',
    userIdTaken: '이미 사용 중인 주소예요.',
    userIdReserved: '쓸 수 없는 주소예요.',
    userIdInvalid: '소문자로 시작하고 3~20자여야 해요.',
    userNameLabel: '표시할 이름',
    userNameHelp: '언제든 바꿀 수 있어요.',
    submit: '프로필 만들기',
  },

  merge: {
    title: '이미 저장된 목록이 있어요',
    lead: '이 브라우저에서 고른 것과 계정에 저장된 것이 달라요. 어느 쪽을 남길지 골라주세요.',
    autoAdded: '{count}개는 새로 추가됩니다.',
    conflictTitle: '겹치는 카테고리',
    conflictLead: '{count}개 카테고리에서 다른 걸 고르셨어요.',
    keepLocal: '이 브라우저에서 고른 것',
    keepServer: '계정에 저장된 것',
    apply: '이대로 저장',
    skipAll: '계정 것 그대로 두기',
    done: '목록을 합쳤어요',
  },

  profile: {
    shareCopy: '주소 복사',
    notFound: '없는 사용자예요',
    notFoundBody: '주소를 다시 확인해 주세요.',
    pickCount: '{count}개',
    emptyOther: '아직 고른 게 없어요.',
  },

  home: {
    timeline: '타임라인',
    picks: '취향',
    analytics: '분석',
    profile: '내 프로필',
  },

  timeline: {
    reorderHint:
      '길게 눌러 끌면 차례를 바꿀 수 있어요. 다른 해로 옮기면 연도도 따라 바뀝니다. 키보드로는 Space로 집고 화살표로 옮깁니다.',
    colorCode: '색 코드',
    colorInvalid: '#0f0f0f 처럼 적어주세요.',
    all: '전체',
    seedTravel: '여행',
    title: '내 타임라인',
    topicReorderHint:
      '주제를 길게 눌러 끌면 차례를 바꿀 수 있어요. 키보드로는 Space로 집고 화살표로 옮깁니다.',
    addTopic: '주제 추가',
    editTopic: '주제',
    topicLabel: '주제 이름',
    topicColor: '색',
    topicDeleted: '주제를 지웠어요',
    deleteTopicConfirm: '{label}, 지울까요?',
    deleteTopicBody: '이 주제의 기록 {count}개도 같이 사라져요.',
    addEvent: '기록 추가',
    editEvent: '기록',
    eventTopic: '주제',
    eventYear: '시작',
    eventTitle: '제목',
    eventNote: '설명',
    eventEnd: '종료',
    usePeriod: '기간 설정',
    eventYearOnly: '연도',
    /** 달을 안 고른 상태. select의 첫 칸에 들어간다 */
    monthNone: '월',
    /** 종료를 안 정한 상태 */
    endNone: '없음',
    endNeedsStartMonth: '시작 달을 먼저 고르면 종료도 정할 수 있어요.',
    monthUnit: '{month}월',
    addUrl: '+ 링크',
    eventUrl: '링크',
    eventUrlHint: 'https로 시작하는 주소만 걸립니다.',
    periodNow: '현재',
    periodMonths: '{count}개월',
    periodYears: '{count}년',
    periodYearsMonths: '{years}년 {months}개월',
    monthInvalid: '달은 1부터 12까지예요.',
    endBeforeStart: '종료는 시작보다 뒤여야 해요.',
    urlInvalid: 'https로 시작하는 주소를 넣어주세요.',
    eventDeleted: '기록을 지웠어요',
    yearInvalid: '1900~2200 사이로 적어주세요.',
  },

  legal: {
    privacyTitle: '개인정보처리방침',
  },

  people: {
    title: '사람 찾기',
    searchLabel: '이름이나 주소',
    narrow: '더 있어요. 이름이나 주소를 더 입력해보세요.',
    noResults: '찾는 사람이 없어요.',
  },

  upgrade: {
    title: '플랜',
    /* 값이 아니라 **이름**으로 부른다. "무료/유료"는 돈 이야기가 먼저 오는데,
       지금은 팔지도 않고 앞으로도 고르는 건 플랜이지 가격이 아니다 */
    basic: '기본',
    /* "프리미엄"은 값이 먼저 읽힌다 — 아직 팔지도 않는 칸에서 "비싸겠네"가
       먼저 오면 표를 읽기도 전에 닫는다. "플러스"는 **더 담긴다**는 뜻이
       이름 안에 있어 옆 칸의 ∞와 같은 말을 한다 */
    plus: '플러스',
    unlimited: '무제한',
    /** 유료 칸 머리 밑의 작은 줄 — 아직 못 산다는 것을 칸에서 바로 말한다 */
    /** 표 오른쪽 열의 머리. 숫자가 `3/5` 꼴이라 "쓰는 중"이면 뜻이 선다 */
    topics: '주제',
    records: '기록',
    connections: '인연',
    soon: '지금은 모든 기능을 무료로 열어 뒀어요. 상한을 더 올리는 건 준비하고 있어요.',
    /* 눈에는 체크와 빗금만 보이지만 읽어 주는 기기에는 말로 준다 */
    included: '됨',
    notIncluded: '아직 안 됨',
  },

  viewers: {
    peek: '보기',
    pageTitle: '분석',
    /** 프로필의 한 줄. **기간은 안 적는다** — 며칠 기준인지는 상세에서 본다 */
    summary: '최근 {count}명이 봤어요',
    recentLabel: '최근 {days}일 동안 본 사람',
    totalLabel: '남아 있는 기록 전부 {count}명',
    none: '아직 아무도 안 봤어요.',
    /* 1분 안쪽. "0분 전"은 사람이 안 쓰는 말이다 */
    justNow: '방금',
  },

  /**
   * 화면에서는 **인연**이라고 부른다.
   *
   * 코드와 API는 `connection`으로 둔다 — 중립적인 자료 이름이라 그대로 두고,
   * 사람에게 보이는 말만 여기서 정한다.
   *
   * "이어진 사람"은 새로 지어낸 말이라 뜻을 짐작하게 만들었고, "친구"는
   * 스승·선배에게 쓰기엔 넘치는 말이었다. **인연은 맺고 끊는 말이 이미 있다** —
   * `인연을 맺다`·`인연을 끊다`가 관용구라 동작 문구가 자연스럽게 나온다
   * ("친구를 삭제했어요"보다 낫다). 인생을 적는 곳이라는 주제와도 맞는다.
   *
   * 세는 자리에는 `명`을 붙인다 — "인연 3"은 말이 안 되고 "인연 3명"은 된다.
   */
  connection: {
    request: '인연 요청',
    waiting: '수락 기다리는 중',
    connected: '인연 끊기',
    accept: '수락',
    reject: '거절',
    /** 내가 보낸 요청을 물릴 때. 버튼 글자가 **하는 일**을 말해야 한다 */
    cancel: '요청 취소',
    requestSent: '요청했어요',
    canceled: '요청을 물렸어요',
    accepted: '인연을 맺었어요',
    rejected: '거절했어요',
    removed: '인연을 끊었어요',
    /** 프로필의 인연 수 — 누르면 상세로 간다 */
    count: '인연 {count}명',
    title: '인연',
    incoming: '나에게 온 요청',
    /** 내 프로필 맨 위의 알림 줄 */
    pendingAlert: '받은 인연 요청 {count}건',
    outgoing: '내가 보낸 요청',
    none: '아직 맺은 인연이 없어요.',
    /** 남의 인연 목록이 비었을 때 */
    noneOther: '아직 맺은 인연이 없어요.',
    lockedHint: '인연을 맺으면 이 사람의 타임라인을 볼 수 있어요.',
  },

  share: {
    /** 공유용 한 장 */
    card: '공유하기',
    /* 나오는 게 주소가 아니라 **그림**이라는 건 시트 제목이 말한다 —
       버튼까지 "이미지로 공유"로 길게 가면 옆의 짧은 버튼들과 안 맞는다 */
    sheetTitle: '이미지로 공유',
    send: '공유',
    square: '1:1',
    story: '9:16',
  },

  settings: {
    themeSection: '테마',
    theme: {
      /* 지금까지 앱이 하던 동작이다 — 이름을 줘야 돌아갈 길이 생긴다 */
      system: '시스템',
      light: '밝게',
      dark: '어둡게',
    },
    privateSection: '공개 범위',
    privateLabel: '비공개 계정',
    privateHelp:
      '잠그면 인연을 맺은 사람만 내 타임라인을 봅니다. 잠그지 않으면 로그인한 누구나 볼 수 있어요.',
    privateOn: '잠갔어요',
    privateOff: '공개로 바꿨어요',
    title: '설정',
    profileSection: '프로필',
    photoLabel: '프로필 사진',
    userNameLabel: '표시할 이름',
    bioLabel: '한마디',
    languageSection: '언어',
    addressSection: '내 주소',
    addressFieldLabel: '공개 주소',
    addressHelp: '소문자, 숫자, - , _ 를 쓸 수 있어요.',
    addressWarning:
      '바꾸면 지금 주소로 들어오던 링크는 열리지 않아요. 옛 주소는 30일간 아무도 가져갈 수 없고, 그 안에는 되돌릴 수 있어요.',
    addressSave: '주소 바꾸기',
    addressSaved: '주소를 바꿨어요',
    saved: '저장했어요',
  },

  error: {
    generic: '문제가 생겼어요. 잠시 후 다시 시도해 주세요.',
    /* 상한에 닿은 것은 고장이 아니다 — **얼마까지인지**를 말해 준다 */
    topicLimit: '주제는 {topics}개까지 만들 수 있어요.',
    eventLimit: '기록은 {events}개까지 담을 수 있어요.',
    eventYearLimit:
      '한 해에는 기록을 {perYear}개까지 담을 수 있어요. 다른 해에는 더 넣을 수 있어요.',
    network: '연결에 실패했어요. 인터넷을 확인해 주세요.',
    notFound: '찾을 수 없어요.',
    loginRequired: '로그인이 필요해요.',
    sessionExpired: '로그인이 만료됐어요. 다시 로그인해 주세요.',
    categoryNotFound: '없는 카테고리예요.',
    categoryNotPickable: '이 카테고리에는 등록할 수 없어요.',
    categoryDeprecated: '더 이상 쓰지 않는 카테고리예요.',
    userIdTaken: '이미 사용 중인 주소예요.',
    userAlreadyExists: '이미 프로필이 있어요.',
    validation: '입력값을 다시 확인해 주세요.',
    required: '필수 항목이에요.',
    tooLong: '너무 깁니다.',
    imageNotUploaded: '사진을 다시 올려주세요.',
    tooManyRequests: '요청이 너무 많아요. 잠시 후 다시 시도해 주세요.',
    serverConfig: '서버 설정에 문제가 있어요. 잠시 후 다시 시도해 주세요.',
  },
} as const;

/**
 * 다른 언어 파일이 따라야 하는 **구조**.
 *
 * `as const`가 값을 리터럴 타입으로 굳히므로 그대로 쓰면 영어 파일이 한국어와
 * 똑같은 문자열이어야 한다고 요구한다. 값 타입만 `string`으로 넓혀
 * "키는 같고 값은 자유"가 되게 한다.
 */
type WidenStrings<T> = {
  [K in keyof T]: T[K] extends string ? string : WidenStrings<T[K]>;
};

export type Messages = WidenStrings<typeof ko>;
